import React, { useState, useEffect, useCallback } from 'react';
import { apAgingApi } from '../services/api';
import '../styles/Panel.css';

function ELAPConsolidationPanel() {
  const [reportingDate, setReportingDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [status, setStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const refreshPeriods = useCallback(async () => {
    try {
      const list = await apAgingApi.listELConsolidated();
      setPeriods(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error listing EL AP console periods:', err);
    }
  }, []);

  useEffect(() => { refreshPeriods(); }, [refreshPeriods]);

  const checkStatus = useCallback(async (date) => {
    if (!date) { setStatus(null); return; }
    setCheckingStatus(true);
    try {
      setStatus(await apAgingApi.statusELConsolidated(date));
    } catch (err) {
      console.error('Error checking EL AP console status:', err);
      setStatus(null);
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  useEffect(() => { checkStatus(reportingDate); }, [reportingDate, checkStatus]);

  const triggerDownload = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleGenerateConsolidated = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const blob = await apAgingApi.generateELConsolidated(reportingDate);
      triggerDownload(blob, `EL_AP_Console_${reportingDate}.xlsx`);
      setResult({
        success: true,
        message: 'EL AP consolidated console generated and downloaded successfully',
        reporting_date: reportingDate,
      });
      refreshPeriods();
      checkStatus(reportingDate);
    } catch (err) {
      console.error('Error generating EL AP consolidated console:', err);
      setError(err.message || 'Failed to generate EL AP consolidated console');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLatest = async () => {
    setDownloading(true);
    setError(null);
    setResult(null);
    try {
      const blob = await apAgingApi.downloadELConsolidated(reportingDate);
      triggerDownload(blob, `EL_AP_Console_${reportingDate}.xlsx`);
      setResult({
        success: true,
        message: 'Latest EL AP console downloaded successfully',
        reporting_date: reportingDate,
      });
    } catch (err) {
      console.error('Error downloading latest EL AP console:', err);
      let msg = `No EL AP console has been generated for ${reportingDate} yet. Generate it first.`;
      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed && parsed.error) msg = parsed.error;
        } catch (_) { /* keep default message */ }
      }
      setError(msg);
    } finally {
      setDownloading(false);
    }
  };

  const busy = loading || downloading;
  const selectedIsGenerated = periods.some((p) => p.reporting_date === reportingDate);

  return (
    <div className="panel">
      <h2>AP Ageing - EL Consolidated Console</h2>
      <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
        Consolidate the latest standalone AP ageing files from S3 for all 20 Everlife (EL)
        group entities for the selected reporting date.
      </p>

      <div className="form-group">
        <label htmlFor="el-ap-consol-date">Reporting Date:</label>
        <input
          id="el-ap-consol-date"
          type="date"
          value={reportingDate}
          onChange={(e) => setReportingDate(e.target.value)}
          disabled={busy}
        />
      </div>

      {checkingStatus ? (
        <p style={{ fontSize: '13px', color: '#888', marginTop: '-4px' }}>Checking this period…</p>
      ) : status && status.available ? (
        <p style={{ fontSize: '13px', color: '#2e7d32', marginTop: '-4px' }}>
          ✓ Already generated for this period
          {status.generated_date ? ` (last: ${new Date(status.generated_date).toLocaleString()})` : ''} — use “Download Latest Generated”.
        </p>
      ) : status ? (
        <p style={{ fontSize: '13px', color: '#b26a00', marginTop: '-4px' }}>
          ● Not generated yet for this period — click “Generate &amp; Download”.
        </p>
      ) : null}

      {periods.length > 0 && (
        <div className="form-group">
          <label htmlFor="el-ap-consol-period">Previously generated periods:</label>
          <select
            id="el-ap-consol-period"
            value={selectedIsGenerated ? reportingDate : ''}
            onChange={(e) => { if (e.target.value) setReportingDate(e.target.value); }}
            disabled={busy}
          >
            <option value="">— pick a generated period to download —</option>
            {periods.map((p) => (
              <option key={p.reporting_date} value={p.reporting_date}>
                {p.reporting_date}
                {p.last_modified ? ` — generated ${new Date(p.last_modified).toLocaleString()}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={handleGenerateConsolidated}
          disabled={busy}
          className="btn btn-primary"
        >
          {loading ? 'Generating...' : 'Generate & Download EL AP Console'}
        </button>
        <button
          onClick={handleDownloadLatest}
          disabled={busy}
          className="btn btn-secondary"
        >
          {downloading ? 'Downloading...' : 'Download Latest Generated'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className="success-message">
          <strong>Success:</strong> {result.message}
          <br />
          <small>Reporting Date: {result.reporting_date}</small>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <h4>How periods work:</h4>
        <ul style={{ marginLeft: '20px', color: '#555' }}>
          <li><strong>A new / different period:</strong> pick the date, then <em>Generate &amp; Download</em> — it
            builds from that period's standalone files and stores it for later.</li>
          <li><strong>An already-generated period:</strong> pick it from <em>Previously generated periods</em>
            (or set the date), then <em>Download Latest Generated</em> — no regeneration.</li>
        </ul>
        <h4 style={{ marginTop: '16px' }}>Console Contents:</h4>
        <ul style={{ marginLeft: '20px', color: '#555' }}>
          <li><strong>Consolidated AP Ageing Detail</strong> — all entities stacked, with a highlighted
            <em> Entity Code</em> column (filterable) and a <em>Balance Due (USD)</em> column driven by an
            editable FX&nbsp;→&nbsp;USD block at the top of the sheet.</li>
          <li><strong>Bucket Amounts (Local)</strong> — ageing-bucket totals in each entity's own currency.</li>
          <li><strong>Bucket Amounts (USD)</strong> — ageing-bucket totals consolidated in USD.</li>
          <li><strong>Unique Vendor Totals</strong> — total balance per unique vendor (local + USD).</li>
          <li><strong>Console Info</strong> — which entities were included / missing for the period.</li>
        </ul>
      </div>
    </div>
  );
}

export default ELAPConsolidationPanel;
